import { useEffect, useState } from 'react'
import { FormControl, InputLabel, Select, MenuItem, Box, Chip } from '@mui/material'
import { getObjectsWithFilter } from '../../api/apiClientCrud'

/**
 * Composant de sélection multiple avec gestion des IDs sous forme de chaîne (comma-separated)
 * Sécurisé contre les doublons et les objets mal sérialisés.
 */
function MultiSelectField({ data, mode, field, onValueChange, isError }) {
  const [selectOptions, setSelectOptions] = useState([])

  const isReadOnly = mode === 'view' || field.readOnly || field.readOnlyWhen?.(data)
  const cleanData = Object.fromEntries(Object.entries(data).filter(([, v]) => v !== ''))

  // Récupération des options via API ou données statiques
  useEffect(() => {
    if (field.type !== 'multiselect') return

    const fetchOptions = async () => {
      try {
        const filter = field.options?.filter?.(cleanData) ?? null
        const subfilter = field.options?.subFilter?.(cleanData) ?? null
        const res = await getObjectsWithFilter(field.options.object, filter, subfilter)
        setSelectOptions(res.data[field.options.object] ?? res.data)
      } catch (err) {
        setSelectOptions([])
      }
    }

    if (field.options?.type === 'api') {
      fetchOptions()
    } else if (field.options?.type === 'data') {
      setSelectOptions(field.options.data ?? [])
    }
    // On surveille les dépendances et le nom du champ pour éviter les rechargements inutiles
  }, [data[field.depends ?? ''], field.name])

  // Normalisation au montage : si la valeur brute est un tableau d'objets,
  // on la convertit immédiatement en string CSV d'IDs avant toute soumission.
  useEffect(() => {
    const rawValue = data[field.name]
    // On n'agit que si la valeur est un tableau contenant des objets Sequelize non sérialisés
    if (!Array.isArray(rawValue) || !rawValue.some((v) => v && typeof v === 'object')) return

    triggerChange(getInitialValues())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data[field.name]])
  /**
   * Extrait et normalise les valeurs sélectionnées depuis les données parentes.
   * Transforme les objets Sequelize ou les chaînes brutes en un tableau d'IDs (String).
   */
  const getInitialValues = () => {
    const rawValue = data[field.name]
    let valuesArray = []

    if (Array.isArray(rawValue)) {
      valuesArray = rawValue
    } else if (typeof rawValue === 'string' && rawValue !== '') {
      valuesArray = rawValue.split(',')
    } else {
      valuesArray = field.defaultValue?.() ?? []
    }

    // Normalisation : on ne garde que des IDs valides (jamais d'objets ou de [object Object])
    return valuesArray
      .map((val) => (val && typeof val === 'object' ? val.id : val))
      .filter((val) => val !== undefined && val !== null && val !== '' && val !== '[object Object]')
      .map(String) // Force la string pour la comparaison avec les MenuItem
  }

  const selectedValues = getInitialValues()

  /**
   * Déclenche la mise à jour vers le parent uniquement si un changement réel est détecté.
   */
  const triggerChange = (nextValues) => {
    // Nettoyage des valeurs entrantes
    const cleanValues = nextValues
      .map((val) => (val && typeof val === 'object' ? val.id : val))
      .filter((val) => val !== undefined && val !== null && val !== '' && val !== '[object Object]')

    // Unicité et tri pour une comparaison stable
    const uniqueValues = [...new Set(cleanValues)].map(String).sort()
    const newValueString = uniqueValues.join(',')

    // Comparaison avec la valeur actuelle dans le state global
    // Si identique, on ne déclenche rien pour éviter les erreurs SQL sur un formulaire "propre"
    const currentValueString = String(data[field.name] || '')
    if (newValueString === currentValueString) return

    onValueChange({
      target: {
        name: field.name,
        value: newValueString
      }
    })
  }

  const handleDelete = (valueToDelete) => {
    const newValues = selectedValues.filter((val) => String(val) !== String(valueToDelete))
    triggerChange(newValues)
  }

  const renderMenuItems = () => {
    if (field.options?.type === 'custom' && field.options.component) {
      return field.options.component(data)
    }
    return selectOptions.map((item) => {
      const itemValue = String(item[field.options.returnField || 'id'])
      return (
        <MenuItem key={itemValue} value={itemValue}>
          {item[field.options.value || 'name']}
        </MenuItem>
      )
    })
  }

  return (
    <FormControl
      variant={mode === 'view' ? 'standard' : 'outlined'}
      fullWidth={field.fullWidth ?? false}
      required={field.required ?? false}
      error={isError}
      sx={{ my: 1, minWidth: 120 }}
    >
      <InputLabel id={`${field.name}-label`} error={isError}>
        {field.label}
      </InputLabel>
      <Select
        labelId={`${field.name}-label`}
        id={`${field.name}-select`}
        name={field.name}
        label={field.label}
        multiple
        value={selectedValues}
        onChange={(e) => {
          const val = e.target.value
          triggerChange(Array.isArray(val) ? val : [val])
        }}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((val) => {
              const option = selectOptions.find((o) => String(o.id) === String(val))
              return (
                <Chip
                  key={val}
                  label={option ? option[field.options.value || 'name'] : val}
                  onDelete={isReadOnly ? undefined : () => handleDelete(val)}
                  // StopPropagation pour éviter d'ouvrir le menu au clic sur la croix
                  onMouseDown={(e) => e.stopPropagation()}
                />
              )
            })}
          </Box>
        )}
        disabled={isReadOnly}
        error={isError}
      >
        {renderMenuItems()}
      </Select>
    </FormControl>
  )
}

export default MultiSelectField
